import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import {
  formatPhoneNumber,
  formatPhoneNumberForWhatsApp,
  formatPhoneNumberFromWhatsApp,
} from "../helpers/phone";
import toastConfig from "../../../../../components/shared/constants/toaster-config";
import {
  CONTACT_FILE_PROCESSOR_TABS,
  MAX_SIZE_FILE_CONTACTS_IN_BYTES,
} from "../constants/contacts";
import { getCostCampaign } from "../helpers/campaign";

/**
 * Validates a contact, checking phone format and required variables.
 * @param {string} phone - Phone number to validate
 * @param {Array<string>} variables - Array of variables to validate
 * @param {Array<string>} defaultVariables - Array of variable names
 * @returns {Object} Validation result
 */
const validateContact = ({
  phone,
  variables,
  defaultVariables,
  defaultCountry,
}) => {
  const formatPhone = formatPhoneNumberFromWhatsApp(phone);

  const { number, country, format, error } = formatPhoneNumber(
    formatPhone,
    defaultCountry,
  );

  if (error || !number) {
    return {
      isValid: false,
      phone: phone,
      country: null,
      variables: {},
      reason: error,
      formattedPhone: null,
    };
  }

  const requiredVarCount = defaultVariables.length;

  if (requiredVarCount === 0) {
    return {
      isValid: true,
      phone: number,
      country: country,
      variables: {},
      reason: null,
      formattedPhone: format,
    };
  }

  let missingVars = false;
  let missingVarsCount = 0;
  const formatVariables = {};

  for (let v = 0; v < requiredVarCount; v++) {
    const val = variables[v];

    if (val === undefined || val === null || val === "") {
      missingVars = true;
      missingVarsCount++;
      continue;
    }

    formatVariables[`var${v}`] = val;
  }

  if (missingVars) {
    return {
      isValid: false,
      phone: number,
      country: country,
      variables: formatVariables,
      reason: `Faltan variables (${missingVarsCount})`,
      formattedPhone: format,
    };
  }

  return {
    isValid: true,
    phone: number,
    country: country,
    variables: formatVariables,
    reason: null,
    formattedPhone: format,
  };
};

const isPhoneColumn = (colIndex) => colIndex === 0;
const isNumericCell = (cell) => cell.t === "n";

const readCellValue = (cell, colIndex) => {
  if (!cell) return "";

  if (isPhoneColumn(colIndex) && isNumericCell(cell))
    return Math.round(cell.v).toString();

  if (cell.w !== undefined) return cell.w;

  if (cell.v !== undefined) return String(cell.v);

  return "";
};

const parseWorksheetRows = (worksheet) => {
  const range = XLSX.utils.decode_range(worksheet["!ref"] || "A1");
  const rows = [];

  for (let rowIndex = range.s.r; rowIndex <= range.e.r; rowIndex++) {
    const row = [];

    for (let colIndex = range.s.c; colIndex <= range.e.c; colIndex++) {
      const address = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex });
      row.push(readCellValue(worksheet[address], colIndex));
    }

    rows.push(row);
  }

  return rows;
};

export const useContactFileProcessor = ({
  defaultContactsFile = null,
  defaultContacts = [],
  defaultInvalidContacts = [],
  defaultCampaignCost = 0,
  defaultVariables = [],
  defaultCountry = "MX",
  templateCategory,
  onReupload,
  onSuccessfullyProcessedContacts,
}) => {
  const [error, setError] = useState(null);
  // file state
  const [selectedFile, setSelectedFile] = useState(defaultContactsFile);
  const [isProcessing, setIsProcessing] = useState(false);
  // contacts state
  const [validContacts, setValidContacts] = useState(defaultContacts);
  const [invalidContacts, setInvalidContacts] = useState(
    defaultInvalidContacts,
  );
  const [countryStats, setCountryStats] = useState([]);
  // campaign cost
  const [campaignCostData, setCampaignCostData] = useState(
    defaultCampaignCost,
  );
  // pending duplicates state
  const [pendingDuplicates, setPendingDuplicates] = useState(null);

  const channelId = useSelector((state) => state.campaign.create.channelId);

  /**
   * Procesa el archivo Excel y extrae los contactos separando válidos de inválidos
   * @param {File} file - Archivo Excel a procesar
   */
  const processExcelFile = async (file) => {
    setIsProcessing(true);
    setValidContacts([]);
    setInvalidContacts([]);
    setCountryStats([]);

    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, {
          type: "array",
          cellText: true,
          codepage: 65001,
        });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = parseWorksheetRows(worksheet);

        if (jsonData.length === 0) {
          toast.error("El archivo está vacío", toastConfig);
          return;
        }

        const processedValid = [];
        const processedInvalid = [];
        const duplicates = [];
        const validPhonesSet = new Set();

        for (let i = 0; i < jsonData.length; i++) {
          const row = jsonData[i];

          if (!row || row.length === 0) continue;

          const rawPhone = String(row[0]);

          if (!rawPhone) continue;

          // Simple heuristic: if it contains letters and is row 0, it's a header
          if (i === 0 && /[a-zA-Z]/.test(rawPhone) && !/\d/.test(rawPhone)) {
            continue;
          }

          const restVariables = row.slice(1);

          const validation = validateContact({
            phone: rawPhone,
            variables: restVariables,
            defaultVariables,
            defaultCountry,
          });

          if (!validation.isValid) {
            processedInvalid.push({
              phone: validation.phone,
              reason: validation.reason,
              row: i + 1,
              data: row,
            });
            continue;
          }

          // Use WhatsApp format for duplicate check to ensure consistency
          const formattedPhoneToWhatsApp = formatPhoneNumberForWhatsApp(
            validation.formattedPhone,
          );

          if (validPhonesSet.has(formattedPhoneToWhatsApp)) {
            duplicates.push({
              // valid-contact shape
              phone: formattedPhoneToWhatsApp,
              country: validation.country,
              originalPhone: rawPhone,
              variables: validation.variables,
              // invalid shape fields
              rawPhone,
              row: i + 1,
              data: row,
            });
            continue;
          }

          validPhonesSet.add(formattedPhoneToWhatsApp);

          const contact = {
            phone: formattedPhoneToWhatsApp,
            country: validation.country,
            originalPhone: rawPhone,
            variables: validation.variables,
          };

          processedValid.push(contact);
        }

        if (processedValid.length === 0 && processedInvalid.length === 0 && duplicates.length === 0) {
          toast.error(
            "No se encontraron datos procesables en el archivo",
            toastConfig,
          );
          return;
        }

        if (duplicates.length > 0) {
          if (processedInvalid.length > 0) {
            toast.warning(
              `Se encontraron ${processedInvalid.length} filas con errores`,
              toastConfig,
            );
          }

          setValidContacts(processedValid);
          setInvalidContacts(processedInvalid);
          setPendingDuplicates(duplicates);
          return;
        }

        if (processedInvalid.length > 0) {
          toast.warning(
            `Se encontraron ${processedInvalid.length} filas con errores`,
            toastConfig,
          );
        }

        setValidContacts(processedValid);
        setInvalidContacts(processedInvalid);
        onSuccessfullyProcessedContacts(processedValid, processedInvalid);
      } catch (error) {
        console.error(error);

        toast.error("Error al procesar el archivo Excel", toastConfig);
      } finally {
        setIsProcessing(false);
      }
    };

    reader.onerror = () => {
      toast.error("Hubo un error al leer el archivo", toastConfig);
      setIsProcessing(false);
    };

    reader.readAsArrayBuffer(file);
  };

  const handleEditContact = (index, type, field, value) => {
    // --- VALID CONTACTS EDIT ---
    if (type === CONTACT_FILE_PROCESSOR_TABS.VALID) {
      const newContacts = [...validContacts];
      const contact = { ...newContacts[index] };

      if (field === "phone") {
        contact.phone = value;
        const { number, country, error } = formatPhoneNumber(
          value,
          defaultCountry,
        );

        if (!error && number) {
          // Ensure we use the same WA formatting as file import
          contact.phone = formatPhoneNumberForWhatsApp(number);
          contact.country = country;
        }

        if (contact.isDuplicate && contact.phone !== newContacts[index].phone) {
          delete contact.isDuplicate;
        }
      }

      if (field.startsWith("var")) {
        contact.variables = {
          ...contact.variables,
          [field]: value,
        };
      }

      newContacts[index] = contact;
      setValidContacts(newContacts);
      return;
    }

    // --- INVALID CONTACTS EDIT ---
    const newInvalid = [...invalidContacts];
    const item = { ...newInvalid[index] };

    // Update raw data first to reflect user edits
    if (field === "phone") {
      item.phone = value;
      item.data[0] = value;
    }

    if (field.startsWith("var")) {
      const varIndex = parseInt(field.replace("var", ""));
      item.data[varIndex] = value;
    }

    const phoneRaw = formatPhoneNumberFromWhatsApp(String(item.phone));
    const variablesRaw = item.data.slice(1);

    // Re-validate using the helper
    const validation = validateContact({
      phone: phoneRaw,
      variables: variablesRaw,
      defaultVariables,
      defaultCountry,
    });

    // Still invalid, update reason
    if (!validation.isValid) {
      item.reason = validation.reason;
      newInvalid[index] = item;
      setInvalidContacts(newInvalid);

      return;
    }

    const waPhone = formatPhoneNumberForWhatsApp(validation.formattedPhone);

    const newValidContact = {
      phone: waPhone,
      country: validation.country,
      originalPhone: item.phone,
      variables: validation.variables,
    };

    newInvalid.splice(index, 1);
    setInvalidContacts(newInvalid);

    setValidContacts([...validContacts, newValidContact]);
    toast.success("Contacto corregido", toastConfig);
  };

  const generateExcelFile = (data, sheetName, fileName) => {
    if (!data || data.length === 0) return;

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    XLSX.writeFile(wb, fileName);
  };

  const downloadInvalidContacts = () => {
    const data = invalidContacts.map((item) => {
      const variables = {};

      if (item.data) {
        defaultVariables.forEach((_, idx) => {
          variables[`Variable ${idx + 1}`] = item.data[idx + 1] || "";
        });
      }

      return {
        "Fila Original": item.row,
        Teléfono: item.phone,
        Error: item.reason,
        ...variables,
      };
    });

    generateExcelFile(data, "Errores", "errores_contactos.xlsx");
  };

  const downloadValidContacts = () => {
    const data = validContacts.map((item) => ({
      Teléfono: item.phone,
      País: item.country || "",
      ...(item.variables || {}),
    }));

    generateExcelFile(data, "Contactos Válidos", "contactos_validos.xlsx");
  };

  const deleteContact = (index, type) => {
    if (type === CONTACT_FILE_PROCESSOR_TABS.VALID) {
      const newContacts = [...validContacts];

      newContacts.splice(index, 1);
      setValidContacts(newContacts);
    }

    if (type === CONTACT_FILE_PROCESSOR_TABS.INVALID) {
      const newInvalid = [...invalidContacts];

      newInvalid.splice(index, 1);
      setInvalidContacts(newInvalid);
    }

    toast.success("Contacto eliminado", toastConfig);
  };

  /**
   * Handles the selection of a contact file, validating its type and size.
   *
   * @param {File} file - The file to process.
   */
  const handleFileChange = (file) => {
    if (!file) {
      toast.error("No se seleccionó ningún archivo", toastConfig);
      return;
    }

    const validTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
      "text/csv",
    ];

    if (
      !validTypes.includes(file.type) &&
      !file.name.match(/\.(xlsx|xls|csv)$/i)
    ) {
      toast.error("Formato no válido. Use .xlsx, .xls o .csv", toastConfig);
      return;
    }

    if (file.size > MAX_SIZE_FILE_CONTACTS_IN_BYTES) {
      toast.error(
        `El archivo excede el tamaño máximo de ${MAX_SIZE_FILE_CONTACTS_IN_BYTES / 1024 / 1024}MB`,
        toastConfig,
      );
      return;
    }

    setSelectedFile(file);
    processExcelFile(file);
  };

  const handleReset = () => {
    setSelectedFile(null);
    setValidContacts([]);
    setInvalidContacts([]);
    setCountryStats([]);
    setCampaignCostData(null);
    setPendingDuplicates(null);

    onReupload();
  };

  const resolveDuplicates = (keep) => {
    if (!pendingDuplicates) return;

    const currentValid = validContacts;
    const currentInvalid = invalidContacts;

    let finalValid;
    let finalInvalid;

    if (keep) {
      const duplicatesAsValid = pendingDuplicates.map((d) => ({
        phone: d.phone,
        country: d.country,
        originalPhone: d.originalPhone,
        variables: d.variables,
        isDuplicate: true,
      }));

      finalValid = [...currentValid, ...duplicatesAsValid];
      finalInvalid = currentInvalid;
    } else {
      const duplicatesAsInvalid = pendingDuplicates.map((d) => ({
        phone: d.rawPhone,
        reason: "Número duplicado en el archivo",
        row: d.row,
        data: d.data,
      }));

      finalValid = currentValid;
      finalInvalid = [...currentInvalid, ...duplicatesAsInvalid];
    }

    setPendingDuplicates(null);
    setValidContacts(finalValid);
    setInvalidContacts(finalInvalid);
    onSuccessfullyProcessedContacts(finalValid, finalInvalid);
  };

  const setCostCampaign = async () => {
    const { data, error } = await getCostCampaign(
      channelId,
      countryStats,
      templateCategory,
    );

    if (error) {
      setError("Hubo un error al calcular el costo, contacte al soporte");

      toast.error(
        "Hubo un error al calcular el costo, contacte al soporte",
        toastConfig,
      );
      return;
    }

    setCampaignCostData(data);
  };

  /**
   * Updates country statistics based on the valid contacts list.
   * Aggregates the number of contacts per country and updates the countryStats state.
   * Skips update while duplicates are pending resolution.
   */
  useEffect(() => {
    if (pendingDuplicates !== null) return;

    const statsMap = new Map();

    validContacts.forEach(({ country }) => {
      if (country) {
        statsMap.set(country, (statsMap.get(country) || 0) + 1);
      }
    });

    const stats = Array.from(statsMap, ([country, quantity]) => ({
      country,
      quantity,
    }));

    setCountryStats(stats);
  }, [validContacts, pendingDuplicates]);

  /**
   * Calculates the campaign cost every time the country statistics change.
   * Only performs the request if the statistics list is not empty.
   */
  useEffect(() => {
    if (countryStats.length === 0) return;

    setCostCampaign();
  }, [countryStats]);

  return {
    // data
    selectedFile,
    isProcessing,
    validContacts,
    invalidContacts,
    countryStats,
    campaignCostData,
    error,
    pendingDuplicates,

    // methods
    handleFileChange,
    handleReset,
    handleEditContact,
    deleteContact,
    downloadInvalidContacts,
    downloadValidContacts,
    resolveDuplicates,
  };
};
